import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, Upload, Download, ArrowLeft, RefreshCw, 
  CheckCircle, AlertCircle, Eye, Truck, History, 
  Trash2, Clock, User, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TxtConvPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Fetch history on mount
  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/txt-converter/history`, {
        withCredentials: true
      });
      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Only admins should access this page
  if (!user || !['master_admin', 'admin'].includes(user.role_id)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground">Admin access required</p>
          <Button onClick={() => navigate('/hub')} className="mt-4">
            Go to Hub
          </Button>
        </div>
      </div>
    );
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.txt')) {
        toast.error('Only TXT files are allowed');
        return;
      }
      setSelectedFile(file);
      setPreview(null);
      setError(null);
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/txt-converter/preview`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreview(response.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to preview file';
      setError(msg);
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleConvert = async () => {
    if (!selectedFile) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/txt-converter/process`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = selectedFile.name.replace('.txt', '_converted.txt');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('File converted and downloaded!');
      
      // Refresh history after successful conversion
      fetchHistory();
      
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to convert file';
      setError(msg);
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.txt')) {
        toast.error('Only TXT files are allowed');
        return;
      }
      setSelectedFile(file);
      setPreview(null);
      setError(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const deleteHistoryRecord = async (historyId) => {
    if (!window.confirm('Delete this record from history?')) return;
    
    try {
      await axios.delete(`${API}/txt-converter/history/${historyId}`, {
        withCredentials: true
      });
      toast.success('Record deleted');
      fetchHistory();
    } catch (err) {
      toast.error('Failed to delete record');
    }
  };

  const clearAllHistory = async () => {
    if (!window.confirm('Clear ALL conversion history? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/txt-converter/history`, {
        withCredentials: true
      });
      toast.success('History cleared');
      setHistory([]);
    } catch (err) {
      toast.error('Failed to clear history');
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/hub')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold">TXT Conv.</h1>
              <p className="text-xs text-muted-foreground">Fuel File Unit Converter</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="gap-2"
          >
            <History className="w-4 h-4" />
            History ({history.length})
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* History Panel */}
        {showHistory && (
          <div className="bg-card/60 backdrop-blur-xl rounded-2xl p-6 border border-border/30 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <History className="w-5 h-5 text-accent" />
                Conversion History
              </h2>
              {history.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearAllHistory} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4 mr-1" /> Clear All
                </Button>
              )}
            </div>
            
            {loadingHistory ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Loading history...</p>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-muted-foreground">No conversions yet</p>
                <p className="text-sm text-muted-foreground/70">Convert a file to see history here</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {history.map((record) => (
                  <div 
                    key={record.history_id} 
                    className={`p-4 rounded-xl border ${
                      record.status === 'success' 
                        ? 'bg-green-500/5 border-green-500/20' 
                        : 'bg-red-500/5 border-red-500/20'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {record.status === 'success' ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          )}
                          <p className="font-medium truncate">{record.original_filename}</p>
                        </div>
                        
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(record.converted_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {record.converted_by_name || 'Unknown'}
                          </span>
                          {record.units_converted !== undefined && (
                            <span className="text-accent font-medium">
                              {record.units_converted} units converted
                            </span>
                          )}
                          {record.file_size_bytes && (
                            <span>
                              {(record.file_size_bytes / 1024).toFixed(1)} KB
                            </span>
                          )}
                        </div>
                        
                        {record.status === 'failed' && record.error && (
                          <p className="text-xs text-red-400 mt-1">Error: {record.error}</p>
                        )}
                        
                        {record.units_list && record.units_list.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground mb-1">Units converted:</p>
                            <div className="flex flex-wrap gap-1">
                              {record.units_list.slice(0, 8).map((unit, i) => (
                                <span key={i} className="px-2 py-0.5 bg-secondary/50 rounded text-xs font-mono">
                                  {unit}
                                </span>
                              ))}
                              {record.units_list.length > 8 && (
                                <span className="px-2 py-0.5 text-xs text-muted-foreground">
                                  +{record.units_list.length - 8} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => deleteHistoryRecord(record.history_id)}
                        className="text-muted-foreground hover:text-red-400 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="bg-card/60 backdrop-blur-xl rounded-2xl p-6 border border-border/30 mb-6">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Truck className="w-5 h-5 text-accent" />
            How it works
          </h2>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>This tool processes fuel TXT files by adding <span className="text-accent font-mono">*</span> in front of every UNIT number:</p>
            <div className="bg-secondary/50 rounded-lg p-3 font-mono text-xs space-y-1">
              <p><span className="text-red-400">Before:</span> UNIT231028</p>
              <p><span className="text-green-400">After:</span> UNIT *231028</p>
            </div>
            <div className="bg-secondary/50 rounded-lg p-3 font-mono text-xs space-y-1">
              <p><span className="text-red-400">Before:</span> UNITD1056</p>
              <p><span className="text-green-400">After:</span> UNIT *D1056</p>
            </div>
            <p className="text-xs mt-3">Everything else in the file stays unchanged.</p>
          </div>
        </div>

        {/* Upload Area */}
        <div 
          className={`bg-card/60 backdrop-blur-xl rounded-2xl border-2 border-dashed transition-colors ${
            selectedFile ? 'border-accent/50 bg-accent/5' : 'border-border/50 hover:border-accent/30'
          } p-8 text-center cursor-pointer`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          data-testid="txt-upload-area"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="txt-file-input"
          />
          
          {selectedFile ? (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-accent/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-accent" />
              </div>
              <div>
                <p className="font-bold text-lg">{selectedFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <p className="text-sm text-accent">Click to select a different file</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto">
                <Upload className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-bold text-lg">Upload Fuel TXT File</p>
                <p className="text-sm text-muted-foreground">
                  Drag & drop or click to select
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-500">Error</p>
              <p className="text-sm text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {selectedFile && (
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={processing}
              className="flex-1 gap-2"
              data-testid="txt-preview-btn"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
              Preview Changes
            </Button>
            <Button
              onClick={handleConvert}
              disabled={processing}
              className="flex-1 gap-2 bg-accent hover:bg-accent/90"
              data-testid="txt-convert-btn"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Convert & Download
            </Button>
          </div>
        )}

        {/* Preview Results */}
        {preview && (
          <div className="mt-6 bg-card/60 backdrop-blur-xl rounded-2xl p-6 border border-border/30">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-accent" />
              Preview ({preview.units_found} units found)
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Before */}
              <div>
                <p className="text-sm font-medium text-red-400 mb-2">Before:</p>
                <div className="bg-secondary/50 rounded-lg p-3 space-y-1 font-mono text-xs max-h-60 overflow-y-auto">
                  {preview.preview.before.map((unit, i) => (
                    <p key={i} className="text-muted-foreground">{unit}</p>
                  ))}
                </div>
              </div>
              
              {/* After */}
              <div>
                <p className="text-sm font-medium text-green-400 mb-2">After:</p>
                <div className="bg-secondary/50 rounded-lg p-3 space-y-1 font-mono text-xs max-h-60 overflow-y-auto">
                  {preview.preview.after.map((unit, i) => (
                    <p key={i} className="text-accent">{unit}</p>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-border/30 text-sm text-muted-foreground">
              <p>Total lines: {preview.total_lines} | Units to convert: {preview.units_found}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
